import { createSearchParams, NavigateOptions, useNavigate } from "react-router-dom";

import { RegisterSteps } from "@/components/features/Authentication/Register/utils";
import { replaceUrlParams } from "@/core/string";
import mobileIntegration from "@/integration";
import {
    Claim,
    Contract,
    ConversationObjectType,
    FeatureType,
    InsuredGood,
    InvoiceCategory,
    LoginState,
    RelatedActivityType,
    RelatedContract,
    TodoCategory,
} from "@/shared/domain";
import { ItsmeUserInfo } from "@/shared/domain/auth";
import {
    ActivitiesPageState,
    AddFamilyMemberPageState,
    AppRoute,
    ChangeAddressPageState,
    ContextualConversationLoaderState,
    ContractDetailPageState,
    ConversationsPageState,
    DetailPageState,
    DocumentsRelatedPageState,
    FeaturePageState,
    InsuredGoodDetailPageState,
    InviteFromBrokerState,
    InvoicePageState,
    PolicyHolderPageState,
    RegisterDefaultState,
    RegisterUnknownItsme,
    RegisterUnknownState,
    ReportClaimPageState,
    ResetPasswordState,
    TarifficationPageState,
} from "@/shared/domain/navigation";
import { useRouterHook } from "@/shared/hooks/useRouterHook";

const withOfficeId = (path: string, officeId?: string) => {
    if (officeId) {
        const queryString = createSearchParams({ o: officeId }).toString();
        return {
            pathname: path,
            search: `?${queryString}`,
        };
    }
    return path;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useNavigation = () => {
    const navigate = useNavigate();
    const isNativeApp = mobileIntegration.isNativeApp();
    const { getStateOrSearch } = useRouterHook<LoginState>();

    return {
        refreshPage() {
            navigate("#", { replace: true });
        },
        // Root
        navToRoot(officeId?: string, options?: NavigateOptions) {
            if (isNativeApp) {
                mobileIntegration.postMessage("submit");
            } else {
                navigate(withOfficeId(AppRoute.Root, officeId), options);
            }
        },

        navToSearchPathname() {
            const pathname = getStateOrSearch("pathname") || AppRoute.Root;
            const search = getStateOrSearch("search");
            navigate({ search, pathname: pathname }, { replace: true });
        },

        navToError(officeId?: string) {
            navigate(withOfficeId(AppRoute.Error, officeId));
        },

        // Dynamic
        navToUrl(url: string) {
            navigate(url);
        },

        // Claims
        navToClaims(filterId: string) {
            const state: FeaturePageState = {
                filterId,
            };
            return navigate(AppRoute.Claims, { state });
        },

        navToClaimSearch() {
            return navigate(AppRoute.ClaimsSearch);
        },

        navToClaimDetail(claim: Claim) {
            const { policyholderId } = claim;
            const state: DetailPageState = {
                policyholderId,
            };
            const pathname = replaceUrlParams(AppRoute.ClaimDetails, { id: claim.id });
            return navigate(pathname, { state });
        },

        // Itsme
        navToItsmeRegister() {
            return navigate(AppRoute.ItsmeRegister);
        },

        // Invite
        navToInvitePushState(email: string, officeId: string, date: Date) {
            const state: InviteFromBrokerState = {
                email,
                officeId,
                date,
            };
            return navigate(AppRoute.Invite, { state });
        },

        // Invoices
        navToInvoices(filterId: InvoiceCategory) {
            const state: InvoicePageState = {
                filterId,
            };
            return navigate(AppRoute.Invoices, { state });
        },

        navToInvoiceDetail(invoice: MyBrokerApi.Invoice) {
            const state: DetailPageState = {
                policyholderId: invoice.policyholderId,
            };

            const pathname = replaceUrlParams(AppRoute.InvoiceDetails, { id: invoice.id });
            return navigate(pathname, { state });
        },

        navToInvoicesSearch() {
            return navigate(AppRoute.InvoiceSearch);
        },

        // Register
        navToRegisterState(email: string, officeId: string, step: string) {
            const state: RegisterDefaultState = {
                email,
                officeId,
                step,
            };
            return navigate(AppRoute.Register, { state });
        },

        navToRegisterUnknownState(email: string, officeId?: string) {
            const state: RegisterUnknownState = {
                email,
                step: "",
            };
            return navigate(withOfficeId(AppRoute.Register, officeId), { state });
        },

        navToRegisterUnknownItsme(itsmeInfo: ItsmeUserInfo, officeId?: string) {
            const state: RegisterUnknownItsme = {
                itsmeInfo,
                step: RegisterSteps.ITSME_UNKNOWN,
            };
            return navigate(withOfficeId(AppRoute.Register, officeId), { state, replace: true });
        },

        // Contracts
        navToContracts(filterId: string) {
            const state: FeaturePageState = {
                filterId,
            };
            return navigate(AppRoute.Contracts, { state });
        },

        navToContractsSearch() {
            navigate(AppRoute.ContractsSearch);
        },

        navToContractDetail(contract: Contract | RelatedContract) {
            const { id, policyholderId, state: contractState } = contract;

            let state: ContractDetailPageState = {
                policyholderId,
                categoryCode: "",
                stateCode: contractState.id,
            };

            if ("categories" in contract) {
                state = {
                    ...state,
                    categoryCode: contract.categories?.length && contract.categories[0].id,
                };
            }

            const pathname = replaceUrlParams(AppRoute.ContractDetails, { id });
            return navigate(pathname, { state });
        },

        // Conversation
        navToConversations() {
            navigate(AppRoute.Conversations);
        },

        navToConversationDetail({
            conversationId,
            policyholderId,
            title,
            objectId,
            objectType,
        }: {
            conversationId: string;
            policyholderId: number | string;
            title: string;
            objectId?: string;
            objectType?: ConversationObjectType;
        }) {
            const state: ConversationsPageState = {
                title,
                objectId,
                objectType,
                policyholderId: +policyholderId,
            };

            const pathname = replaceUrlParams(AppRoute.ConversationDetail, { id: conversationId });
            return navigate(pathname, { state, replace: true });
        },

        navToContextualConversation(
            objectId: string,
            objectType: ConversationObjectType,
            objectTitle: string,
            policyholderId: number | string,
        ) {
            const state: ContextualConversationLoaderState = {
                objectTitle,
                policyholderId: +policyholderId,
            };

            const pathname = replaceUrlParams(AppRoute.ConversationContextual, { objectType, objectId });
            return navigate(pathname, { state, replace: true });
        },

        // Documents
        navToCustomerCard() {
            const state: PolicyHolderPageState = { type: TodoCategory.CUSTOMER_CARD };
            return navigate(AppRoute.CustomerCard, { state });
        },

        navToCustomerCardDetails(policyholderId: number, replace = false) {
            const pathname = replaceUrlParams(AppRoute.CustomerCardDetail, { id: policyholderId });
            return navigate(pathname, { replace });
        },

        navToTariffication(policyholders?: number[]) {
            const state: PolicyHolderPageState = {
                type: TodoCategory.TARIFFICATION,
                policyholders,
            };
            return navigate(AppRoute.Tariffication, { state });
        },

        navToTarifficationDetails(policyholderId: number, insuredGoodId: string, replace = false) {
            const state: TarifficationPageState = {
                defaultVehicleId: insuredGoodId,
            };
            const pathname = replaceUrlParams(AppRoute.TarifficationDetail, { id: policyholderId });
            return navigate(pathname, { replace, state });
        },

        navToDataVerification(policyholders?: number[]) {
            const state: PolicyHolderPageState = {
                type: TodoCategory.DATA_VERIFICATION,
                policyholders,
            };
            return navigate(AppRoute.DataVerificationCompany, { state });
        },

        navToDataVerificationDetails(policyholderId: number, replace = false) {
            const pathname = replaceUrlParams(AppRoute.DataVerificationCompanyDetail, { id: policyholderId });
            return navigate(pathname, { replace });
        },

        navToLiability() {
            const state: PolicyHolderPageState = { type: TodoCategory.LIABILITY };
            return navigate(AppRoute.Liability, { state });
        },

        navToLiabilityDetails(policyHolderId: number, replace = false) {
            const pathname = replaceUrlParams(AppRoute.LiabilityDetail, { id: policyHolderId });
            return navigate(pathname, { replace });
        },

        navToCommunicationSalary() {
            const state: PolicyHolderPageState = { type: TodoCategory.COMMUNICATION_SALARY };
            return navigate(AppRoute.CommunicationSalary, { state });
        },

        navToCommunicationSalaryDetails(policyHolderId: number, replace = false) {
            const pathname = replaceUrlParams(AppRoute.CommunicationSalaryDetail, { id: policyHolderId });
            return navigate(pathname, { replace });
        },

        navToPackagesToSignList() {
            return navigate(AppRoute.PackagesToSign);
        },

        navToDocuments() {
            navigate(AppRoute.Documents);
        },

        navToDocumentsSearch() {
            navigate(AppRoute.DocumentsSearch);
        },

        // Insured goods
        navToInsuredGoods(filterId: string) {
            const state: FeaturePageState = {
                filterId,
            };

            return navigate(AppRoute.InsuredGoods, { state });
        },

        navToRelatedActivities(
            id: number | string,
            title: string,
            featureType: RelatedActivityType,
            policyholderId: number,
            ibpAclId: number,
        ) {
            const params = { featureType: `${featureType}s`, id };
            const pathname = replaceUrlParams(AppRoute.RelatedActivities, params);
            const state: ActivitiesPageState = {
                title,
                ibpAclId,
                policyholderId,
                featureType,
            };

            return navigate(pathname, { state });
        },

        navToRelatedDocuments(id: number | string, title: string, filter: FeatureType) {
            const params = { id, filter };
            const pathname = replaceUrlParams(AppRoute.RelatedDocuments, params);
            const state: DocumentsRelatedPageState = {
                title,
            };

            return navigate(pathname, { state });
        },

        navToRelatedDocumentsSearch(id: number | string, title: string, filter: FeatureType) {
            const params = { filter, id };
            const pathname = replaceUrlParams(AppRoute.RelatedDocumentsSearch, params);

            const state: DocumentsRelatedPageState = {
                title,
            };

            return navigate(pathname, { state });
        },

        navToFeatureDocuments(filter: MyBrokerApi.DocumentFilter) {
            const pathname = replaceUrlParams(AppRoute.FeatureDocuments, { filter });

            return navigate(pathname);
        },

        navToFeatureDocumentsSearch(filter: MyBrokerApi.DocumentFilter) {
            const pathname = replaceUrlParams(AppRoute.FeatureDocumentsSearch, { filter });

            return navigate(pathname);
        },

        navToInsuredGoodSearch() {
            return navigate(AppRoute.InsuredGoodsSearch);
        },

        navToInsuredGoodDetail(insuredGood: InsuredGood) {
            const state: InsuredGoodDetailPageState = {
                insuredGoodType: insuredGood.type.id,
                policyholderId: insuredGood.policyholderId,
                canRequestGreenCard: insuredGood.canRequestGreenCard,
            };
            const pathname = replaceUrlParams(AppRoute.InsuredGoodDetails, { id: insuredGood.id });
            return navigate(pathname, { state });
        },

        // Reset password
        navToResetPassword(userStatus: MyBrokerApi.UserStatus) {
            const state: ResetPasswordState = {
                email: userStatus.email,
                officeId: userStatus.officeId,
            };
            return navigate(AppRoute.ResetPassword, { state });
        },

        // Requests

        navToMyRequests() {
            navigate(AppRoute.Requests);
        },
        navToRequestsSearch() {
            navigate(AppRoute.RequestsSearch);
        },

        // App Settings
        navToSettings() {
            navigate(AppRoute.Settings);
        },

        // Policyholders
        navToPolicyholders() {
            navigate(AppRoute.Policyholders);
        },

        // User Policyholder Details
        navToPolicyholderDetail(id) {
            const pathname = replaceUrlParams(AppRoute.PolicyholderDetail, { id });
            navigate(pathname);
        },

        // User Relation Details
        navToRelationDetails(
            userId: number,
            query: {
                policyHolderId: string;
                relationInternalId: string;
                internalpolicyholderId: string;
                typePartyId: string;
            },
        ) {
            const queryString = createSearchParams(query).toString();
            const pathname = replaceUrlParams(AppRoute.RelationDetail, { id: userId });
            const search = `?${queryString}`;

            navigate({
                pathname,
                search,
            });
        },

        // Add broker
        navToAddBroker() {
            navigate(AppRoute.AddBroker);
        },

        // Address Change
        navToAddressChange(policyholderId?: number) {
            const state: ChangeAddressPageState = {
                policyholderId,
            };
            navigate(AppRoute.ChangeAddress, { state });
        },

        // User Personal Details
        navToAddFamilyMember(policyholderId: number, policyholderName: string) {
            const state: AddFamilyMemberPageState = {
                policyholderId,
                policyholderName,
            };
            return navigate(AppRoute.AddFamilyMember, { state });
        },

        // Report Damage Page
        navToReportClaimPage() {
            return navigate(AppRoute.ReportDamage);
        },

        // Report from itemDamage Page
        navToReportClaimPageFor(preSelectedInsuredGoodId: string, preSelectedPolicyholderId: number) {
            const state: ReportClaimPageState = {
                preSelectedInsuredGoodId,
                preSelectedPolicyholderId,
            };
            return navigate(AppRoute.ReportDamage, { state });
        },

        navToReportSchoolClaimPage() {
            return navigate(AppRoute.ReportSchoolDamage);
        },

        navToCookiePolicyPage(officeId?: string) {
            return navigate(withOfficeId(AppRoute.CookiePolicy, officeId));
        },

        navToPrivacyPolicyPage(officeId?: string) {
            return navigate(withOfficeId(AppRoute.PrivacyPolicy, officeId));
        },

        navToTermsOfUsePage(officeId?: string) {
            return navigate(withOfficeId(AppRoute.TermsOfUse, officeId));
        },
    };
};
